import { useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchMyPortfolioOverview,
  type PortfolioAccountRow,
} from "../../Store/Reducers/MyPortfolioOverviewSlice";
import {
  fetchPortfolioMap,
  type PortfolioMapAccount,
} from "../../Store/Reducers/PortfolioMapSlice";
import type { SignalCard } from "../../Store/Reducers/SignalFeedSlice";

/**
 * Joins a `signal-feed` card (tier/key/evidence only) against account
 * context from two other endpoints, replicating the field set the mock's
 * `renderFeedCard()` reads off its local `a` object (my-accounts-portfolio-v4.html
 * lines 8010-8077):
 *   - `al`/`rl`/`cluster`/`renewalDays`/`alStaleDays`/`ah` come from
 *     `my-portfolio/overview.accounts` (`PortfolioAccountRow`) — `portfolio-map`
 *     does not carry AL level, RL aggregate, cluster, or renewal date.
 *   - `targetAl`/`rlConditions`/`bhClass` come from `portfolio-map`
 *     (`PortfolioMapAccount`), extended in Phase 0 to expose
 *     `AccountGoals`/`AccountPublicData` fields already computed server-side.
 * This is a genuine 3-way join (signal-feed × portfolio-map × my-portfolio/
 * overview) — built once here so Actions, the Portfolio Map compact rail, and
 * a future Today feed all share the same join instead of re-deriving it.
 *
 * Not reproduced (no real-API source, intentionally omitted rather than
 * faked): the mock's `alTrend` (↑/↓/→) is parsed client-side from mock DOM
 * text that doesn't exist on any real endpoint — no context field for it.
 */
export interface SignalCardAccountContext {
  al: number | null;
  rl: number | null;
  cluster: string | null;
  renewalDays: number | null;
  alStaleDays: number | null;
  ah: number | null;
  bhClass: "strong" | "stable" | "concern" | "no_data" | null;
  /** Raw BH score (0-100), for surfaces that show it alongside `bhClass`
   * (e.g. the Pre-call Brief's "Business Health: Concern (38)" field —
   * mock line ~8426). Already loaded on `portfolio-map`'s `pm.bh`, just
   * not previously selected into this context. */
  bh: number | null;
  targetAl: number | null;
  rlConditions: string | null;
  /** target_al - al, only when positive (a gap to close) — mirrors the
   * mock's `buildPublicDataSignals().alGap`, computed the same way. */
  alGap: number | null;
  /** Account revenue (My Portfolio overview), for the Pre-call Brief's
   * Renewal tile — the same figure the Total Revenue tile sums. */
  revenue: number | null;
}

/** Days after which an AL assessment counts as stale — the admin's
 * `al-stale-days` rule parameter when the feed carries it, else the
 * engine default (signal_thresholds.AL_STALE_DAYS). */
export function isAlStale(
  staleDays: number | null,
  parameters?: Record<string, number | string>
): boolean {
  const limit = Number(parameters?.["al-stale-days"] ?? 30);
  return staleDays != null && staleDays > (Number.isFinite(limit) ? limit : 30);
}

/**
 * One card's header context. S-03: the values the rules read at the
 * account's latest check win, so the header always agrees with why the card
 * fired; My Accounts data only fills in before an account's first check.
 */
export function accountContextFor(
  card: SignalCard,
  ov?: PortfolioAccountRow,
  pm?: PortfolioMapAccount
): SignalCardAccountContext {
  const ev = card.evaluated_context;
  const al = ev ? ev.al : (ov?.al_level ?? null);
  const targetAl = pm?.target_al ?? null;
  const alGap =
    al != null && targetAl != null && targetAl > al ? targetAl - al : null;
  if (ev)
    return {
      al,
      rl: ev.rl,
      cluster: ev.cluster,
      renewalDays: ev.renewal_days,
      alStaleDays: ev.al_stale_days,
      ah: ev.ah,
      bhClass: ev.bh_class,
      bh: ev.bh,
      targetAl,
      rlConditions: ev.rl_conditions,
      alGap,
      revenue: ov?.revenue ?? null,
    };
  return {
    al,
    rl: ov?.rl_aggregate ?? null,
    cluster: ov?.cluster ?? null,
    renewalDays: ov?.renewal_days ?? null,
    alStaleDays: ov?.al_published_days ?? null,
    ah: ov?.account_health ?? pm?.ah ?? null,
    bhClass: pm?.bh_class ?? null,
    bh: pm?.bh ?? null,
    targetAl,
    rlConditions: pm?.rl_conditions ?? null,
    alGap,
    revenue: ov?.revenue ?? null,
  };
}

export interface SignalCardWithContext {
  card: SignalCard;
  context: SignalCardAccountContext;
}

export function useSignalCardsWithAccountContext(
  cards: SignalCard[]
): SignalCardWithContext[] {
  const dispatch = useAppDispatch();
  const portfolioMapAccounts = useAppSelector(s => s.portfolioMap.accounts);
  const portfolioMapLoading = useAppSelector(s => s.portfolioMap.loading);
  const overviewData = useAppSelector(s => s.myPortfolioOverview.data);
  const overviewLoading = useAppSelector(s => s.myPortfolioOverview.loading);

  useEffect(() => {
    if (portfolioMapAccounts.length === 0 && !portfolioMapLoading) {
      void dispatch(fetchPortfolioMap());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (!overviewData && !overviewLoading) {
      void dispatch(fetchMyPortfolioOverview());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const pmByAccount = useMemo(() => {
    const map = new Map<string, (typeof portfolioMapAccounts)[number]>();
    portfolioMapAccounts.forEach(a => map.set(a.account_id, a));
    return map;
  }, [portfolioMapAccounts]);

  const ovByAccount = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof overviewData>["accounts"][number]
    >();
    (overviewData?.accounts ?? []).forEach(a => map.set(a.customer_id, a));
    return map;
  }, [overviewData]);

  return useMemo(
    () =>
      cards.map(card => {
        const context = accountContextFor(
          card,
          ovByAccount.get(card.account_id),
          pmByAccount.get(card.account_id)
        );
        return { card, context };
      }),
    [cards, pmByAccount, ovByAccount]
  );
}
